/**
 * Cross-tabulation of two categorical variables.
 *
 * Reads two columns (pasted as pairs, or as two separate lists), counts every
 * combination, and turns the counts into the three percentage tables SPSS and R's
 * `prop.table` give: row percentages (each row sums to 100), column percentages (each
 * column sums to 100) and total percentages (the whole table sums to 100). The margins
 * follow SPSS's convention: in a row-percentage table the "Total" column is 100% and the
 * "Total" row shows how all cases split across the columns, and likewise for columns.
 *
 * The APA table (`apaCrosstabRows`) is rows of text for `buildApaTable` in
 * `paper-formatting/apa-table`: APA 7 asks for a numbered, titled table with rules only
 * above and below the headings and under the last row, and a note saying what the cell
 * percentages are.
 */

const LINE_BREAK = /\r?\n/;
const MAX_CATEGORIES = 25;

export interface Pair {
  a: string;
  b: string;
}

export interface ParsedPairs {
  pairs: Pair[];
  rowName: string | null;
  colName: string | null;
  /** Lines that did not contain two fields. */
  unreadable: number;
  /** Pairs dropped because one field was blank. */
  missing: number;
  /** Set when two lists differ in length: [lines in A, lines in B]. */
  mismatch: [number, number] | null;
}

function delimiterOf(line: string): string {
  for (const candidate of ["\t", ";", ","]) {
    if (line.includes(candidate)) {
      return candidate;
    }
  }
  return "\t";
}

/** One observation per line: two fields separated by a tab, semicolon or comma. */
export function parsePairs(text: string, header: boolean): ParsedPairs {
  const lines = text.split(LINE_BREAK).filter((line) => line.trim() !== "");
  const delimiter = lines.length > 0 ? delimiterOf(lines[0]) : "\t";
  const result: ParsedPairs = {
    pairs: [],
    rowName: null,
    colName: null,
    unreadable: 0,
    missing: 0,
    mismatch: null,
  };
  for (const [index, line] of lines.entries()) {
    const fields = line.split(delimiter).map((field) => field.trim());
    if (fields.length < 2) {
      result.unreadable += 1;
    } else if (header && index === 0) {
      result.rowName = fields[0] || null;
      result.colName = fields[1] || null;
    } else if (fields[0] === "" || fields[1] === "") {
      result.missing += 1;
    } else {
      result.pairs.push({ a: fields[0], b: fields[1] });
    }
  }
  return result;
}

export type ParsedLists = ParsedPairs;

function trimTrailingBlanks(lines: string[]): string[] {
  const kept = [...lines];
  while (kept.length > 0 && kept.at(-1)?.trim() === "") {
    kept.pop();
  }
  return kept;
}

/** Two lists, one value per line; a blank line is a missing value. */
export function parseLists(
  listA: string,
  listB: string,
  header: boolean
): ParsedLists {
  const a = trimTrailingBlanks(listA.split(LINE_BREAK));
  const b = trimTrailingBlanks(listB.split(LINE_BREAK));
  let rowName: string | null = null;
  let colName: string | null = null;
  if (header) {
    rowName = a.shift()?.trim() || null;
    colName = b.shift()?.trim() || null;
  }
  const mismatch: [number, number] | null =
    a.length === b.length ? null : [a.length, b.length];
  const pairs: Pair[] = [];
  let missing = 0;
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const left = a[i].trim();
    const right = b[i].trim();
    if (left === "" || right === "") {
      missing += 1;
    } else {
      pairs.push({ a: left, b: right });
    }
  }
  return { pairs, rowName, colName, unreadable: 0, missing, mismatch };
}

export type CategoryOrder = "first" | "alphabetical";

export interface Crosstab {
  rowLabels: string[];
  colLabels: string[];
  counts: number[][];
  rowTotals: number[];
  colTotals: number[];
  total: number;
}

function labelsOf(
  values: readonly string[],
  ignoreCase: boolean,
  order: CategoryOrder
): string[] {
  const seen = new Map<string, string>();
  for (const value of values) {
    const key = ignoreCase ? value.toLowerCase() : value;
    if (!seen.has(key)) {
      seen.set(key, value);
    }
  }
  const labels = [...seen.values()];
  if (order === "alphabetical") {
    labels.sort((x, y) => x.localeCompare(y, undefined, { numeric: true }));
  }
  return labels;
}

/** Null with no pairs; "tooMany" when either variable has more than 25 categories. */
export function buildCrosstab(
  pairs: readonly Pair[],
  options: { ignoreCase: boolean; order: CategoryOrder }
): Crosstab | "tooMany" | null {
  if (pairs.length === 0) {
    return null;
  }
  const { ignoreCase, order } = options;
  const rowLabels = labelsOf(
    pairs.map((pair) => pair.a),
    ignoreCase,
    order
  );
  const colLabels = labelsOf(
    pairs.map((pair) => pair.b),
    ignoreCase,
    order
  );
  if (rowLabels.length > MAX_CATEGORIES || colLabels.length > MAX_CATEGORIES) {
    return "tooMany";
  }
  const key = (value: string) => (ignoreCase ? value.toLowerCase() : value);
  const rowIndex = new Map(rowLabels.map((label, i) => [key(label), i]));
  const colIndex = new Map(colLabels.map((label, i) => [key(label), i]));
  const counts = rowLabels.map(() => colLabels.map(() => 0));
  for (const pair of pairs) {
    const r = rowIndex.get(key(pair.a)) as number;
    const c = colIndex.get(key(pair.b)) as number;
    counts[r][c] += 1;
  }
  return {
    rowLabels,
    colLabels,
    counts,
    rowTotals: counts.map((row) => row.reduce((x, y) => x + y, 0)),
    colTotals: colLabels.map((_, c) =>
      counts.reduce((total, row) => total + row[c], 0)
    ),
    total: pairs.length,
  };
}

export type PercentKind = "row" | "column" | "total";

export interface PercentTable {
  /** Shares from 0 to 1, same shape as the counts. */
  cells: number[][];
  /** The "Total" column: one share per row. */
  rowMargins: number[];
  /** The "Total" row: one share per column. */
  colMargins: number[];
  grand: number;
}

export function percentTable(ct: Crosstab, kind: PercentKind): PercentTable {
  const { counts, rowTotals, colTotals, total } = ct;
  const cells = counts.map((row, r) =>
    row.map((count, c) => {
      if (kind === "row") {
        return count / rowTotals[r];
      }
      return count / (kind === "column" ? colTotals[c] : total);
    })
  );
  const rowShares = rowTotals.map((each) => each / total);
  const colShares = colTotals.map((each) => each / total);
  return {
    cells,
    rowMargins: kind === "row" ? rowTotals.map(() => 1) : rowShares,
    colMargins: kind === "column" ? colTotals.map(() => 1) : colShares,
    grand: 1,
  };
}

/** Rows for the page's counts table: header, one row per category, then Total. */
export function countRows(
  ct: Crosstab,
  totalLabel: string,
  format: (n: number) => string
): string[][] {
  return [
    ...ct.rowLabels.map((label, r) => [
      label,
      ...ct.counts[r].map(format),
      format(ct.rowTotals[r]),
    ]),
    [totalLabel, ...ct.colTotals.map(format), format(ct.total)],
  ];
}

/** Rows for a percentage table in the same layout as `countRows`. */
export function percentRows(
  ct: Crosstab,
  kind: PercentKind,
  totalLabel: string,
  format: (share: number) => string
): string[][] {
  const table = percentTable(ct, kind);
  return [
    ...ct.rowLabels.map((label, r) => [
      label,
      ...table.cells[r].map(format),
      format(table.rowMargins[r]),
    ]),
    [totalLabel, ...table.colMargins.map(format), format(table.grand)],
  ];
}

/**
 * APA cell text is parsed for `^a` (superscript) and `_x_` (italic) markup. A category
 * label is the reader's own text, so neutralise both: underscores become spaces and a
 * zero-width space breaks up "^" followed by a letter.
 */
export function apaSafe(label: string): string {
  return label.replaceAll("_", " ").replaceAll("^", "^​");
}

/**
 * Header row, then one row per category with "n (p%)" in each cell, then the Total row —
 * ready for `buildApaTable`. `formatCount` and `formatPercent` supply locale output.
 */
export function apaCrosstabRows(
  ct: Crosstab,
  kind: PercentKind,
  labels: { stub: string; total: string },
  formatCount: (n: number) => string,
  formatPercent: (share: number) => string
): string[][] {
  const table = percentTable(ct, kind);
  const cell = (count: number, share: number) =>
    `${formatCount(count)} (${formatPercent(share)})`;
  return [
    [apaSafe(labels.stub), ...ct.colLabels.map(apaSafe), apaSafe(labels.total)],
    ...ct.rowLabels.map((label, r) => [
      apaSafe(label),
      ...ct.counts[r].map((count, c) => cell(count, table.cells[r][c])),
      cell(ct.rowTotals[r], table.rowMargins[r]),
    ]),
    [
      apaSafe(labels.total),
      ...ct.colTotals.map((count, c) => cell(count, table.colMargins[c])),
      cell(ct.total, table.grand),
    ],
  ];
}
